import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { useMessagesWithExptAndChoreoDisabled } from '@atlassian/post-office-context/narrow-exposure/use-messages-with-expt-and-choreo-disabled';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import { memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { MessageRenderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { type JiraYourWorkInitialData } from '../api/types';

import { type Placement as JiraYourWorkPlacement } from '../contracts/Placement';

import { getMessage, type Message } from '../utils/get-message';

import { type ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';

type MessageState = {
	message?: Message;
	messages?: JiraYourWorkInitialData['messages'];
	selectionComplete: boolean;
	status: 'loading' | 'done';
};

const selectResponse = (
	messages: JiraYourWorkInitialData['messages'],
	isMigrationEnabled: boolean,
): MessageState => {
	return {
		messages,
		message: isMigrationEnabled ? undefined : getMessage(messages),
		selectionComplete: !isMigrationEnabled,
		status: 'done',
	};
};

export const JiraYourWorkPlacementComponent: (
	props: ErrorBoundariedProps<JiraYourWorkPlacement>,
) => ReactNode = createPlacementComponent<JiraYourWorkPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData, handleClearPreloadedData }) => {
		const isMigrationEnabled =
			FeatureGates.checkGate('post-office_enable_no-choreo-expt-migration') &&
			FeatureGates.checkGate('post-office_enable_narrow-exposure-classic');
		const { data, isLoading } = useInitialData<JiraYourWorkInitialData>(initialData);
		const [{ message: savedMessage, messages, selectionComplete, status }, setMessageState] =
			useState<MessageState>(() =>
				preloadedInitialData
					? selectResponse(preloadedInitialData.messages ?? [], isMigrationEnabled)
					: { selectionComplete: false, status: 'loading' },
			);

		const handleOnDismissed = useCallback(() => {
			setMessageState({ selectionComplete: true, status: 'done' });

			if (typeof handleClearPreloadedData === 'function') {
				handleClearPreloadedData();
			}
		}, [handleClearPreloadedData]);

		const isDone = status === 'done';

		useEffect(() => {
			if (!isLoading && !isDone) {
				setMessageState(selectResponse(data?.messages ?? [], isMigrationEnabled));
			}
		}, [data, isLoading, isDone, isMigrationEnabled]);

		const [selectedMessage] = useMessagesWithExptAndChoreoDisabled(
			!selectionComplete && status === 'done' ? (messages ?? []) : [],
			{ maxMessagesToDisplay: 1 },
		);
		const message = selectionComplete ? savedMessage : selectedMessage;

		useEffect(() => {
			if (status === 'done' && !selectionComplete) {
				// Like the legacy selector, keep this response's decision until it is dismissed.
				setMessageState((previous) =>
					previous.selectionComplete
						? previous
						: { message, selectionComplete: true, status: 'done' },
				);
			}
		}, [message, selectionComplete, status]);

		if (status === 'loading' || !message) {
			return null;
		}

		return (
			<MessageRenderer
				{...message}
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				id={message.messageInstanceId}
				onDismissed={handleOnDismissed}
				SuspenseBoundary={MaybeSuspenseBoundary}
			/>
		);
	}),
	{
		choreographer: {
			isDefaultEnabled: false,
		},
	},
);

export default JiraYourWorkPlacementComponent;
